import React, { useEffect, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Button } from '../../ui/button';
import { Textarea } from '../../ui/textarea';
import { Bot, Image as ImageIcon, Loader2, Plus, Save, Trash2, Upload, Link2 } from 'lucide-react';
import { toast } from 'sonner';
import { getBotWelcome, updateBotWelcome, uploadMedia } from '../../../lib/api';

const EMPTY = { enabled: true, media_url: '', media_type: 'photo', text1: '', text2: '', buttons: [] };

export default function BotSettingSection() {
  const [cfg, setCfg] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    (async () => {
      try { const d = await getBotWelcome(); setCfg({ ...EMPTY, ...d }); }
      catch { /* keep defaults */ }
      finally { setLoading(false); }
    })();
  }, []);

  const set = (patch) => setCfg((c) => ({ ...c, ...patch }));

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const res = await uploadMedia(f);
      const ct = res.content_type || f.type || '';
      const mt = ct === 'image/gif' ? 'animation' : ct.startsWith('video') ? 'video' : 'photo';
      set({ media_url: res.url, media_type: mt });
      toast.success('Media uploaded');
    } catch { toast.error('Upload failed'); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const setBtn = (i, patch) => set({ buttons: cfg.buttons.map((b, idx) => (idx === i ? { ...b, ...patch } : b)) });
  const addBtn = () => set({ buttons: [...cfg.buttons, { label: '', url: '' }] });
  const rmBtn = (i) => set({ buttons: cfg.buttons.filter((_, idx) => idx !== i) });

  const save = async () => {
    setSaving(true);
    try {
      const res = await updateBotWelcome(cfg);
      setCfg({ ...EMPTY, ...res.bot_welcome });
      toast.success('Bot welcome saved');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  if (loading) {
    return <div className="rounded-lg border border-zinc-800 bg-zinc-950 p-4 flex items-center gap-2 text-zinc-400 text-sm"><Loader2 size={16} className="animate-spin" /> Loading bot settings…</div>;
  }

  const isVideo = cfg.media_type === 'video';

  return (
    <div className="space-y-6" data-testid="botsetting-section">
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-zinc-100 flex items-center gap-2"><Bot size={18} /> Bot /start Welcome Message</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="text-zinc-400 text-sm leading-relaxed">
            When anyone taps <span className="font-mono text-zinc-200">/start</span> on your Telegram bot, it sends — in order — your <b className="text-zinc-200">Media</b>, then <b className="text-zinc-200">Text 1</b>, then <b className="text-zinc-200">Text 2</b> with your <b className="text-zinc-200">inline buttons</b> below it. Each is a separate message (media has no caption).
          </div>

          {/* Enable toggle */}
          <div className="flex items-center justify-between p-3 rounded-lg border border-zinc-800 bg-zinc-950">
            <div>
              <div className="text-zinc-100 font-bold text-sm">Enable Welcome Sequence</div>
              <div className="text-zinc-400 text-xs">When off, the bot replies with the default message.</div>
            </div>
            <button type="button" role="switch" aria-checked={cfg.enabled} onClick={() => set({ enabled: !cfg.enabled })}
              className={`relative w-11 h-6 rounded-full transition-colors shrink-0 ${cfg.enabled ? 'bg-emerald-500' : 'bg-zinc-700'}`}
              data-testid="bot-welcome-enabled-toggle">
              <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${cfg.enabled ? 'translate-x-5' : ''}`} />
            </button>
          </div>

          {/* Media */}
          <div className="space-y-2">
            <Label className="text-zinc-300 text-xs flex items-center gap-1.5"><ImageIcon size={13} /> Media (Image / Video / GIF) — sent first, without caption</Label>
            {cfg.media_url && (
              <div className="relative w-full max-w-xs rounded-lg overflow-hidden border border-zinc-800 bg-black">
                {isVideo
                  ? <video src={cfg.media_url} className="w-full max-h-48 object-contain" controls muted />
                  : <img src={cfg.media_url} alt="welcome media" className="w-full max-h-48 object-contain" />}
                <button type="button" onClick={() => set({ media_url: '' })}
                  className="absolute top-1.5 right-1.5 w-7 h-7 rounded-md bg-black/70 hover:bg-red-600 text-white flex items-center justify-center"
                  data-testid="bot-welcome-media-remove">
                  <Trash2 size={13} />
                </button>
              </div>
            )}
            <div className="flex flex-wrap items-center gap-2">
              <input ref={fileRef} type="file" accept="image/*,video/*,.gif" className="hidden" onChange={onFile} data-testid="bot-welcome-media-file" />
              <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading}
                className="border-zinc-700 bg-zinc-950 text-zinc-200 hover:bg-zinc-800" data-testid="bot-welcome-upload-btn">
                {uploading ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Upload size={14} className="mr-1.5" />}
                {uploading ? 'Uploading…' : 'Upload Media'}
              </Button>
              <div className="flex items-center gap-1.5 flex-1 min-w-[220px]">
                <Link2 size={14} className="text-zinc-500 shrink-0" />
                <Input value={cfg.media_url} onChange={(e) => set({ media_url: e.target.value })}
                  placeholder="…or paste a direct media URL"
                  className="bg-zinc-950 border-zinc-800 text-zinc-100 text-xs" data-testid="bot-welcome-media-url" />
              </div>
              <select value={cfg.media_type} onChange={(e) => set({ media_type: e.target.value })}
                className="h-9 rounded-md bg-zinc-950 border border-zinc-800 text-zinc-200 text-xs px-2"
                data-testid="bot-welcome-media-type">
                <option value="photo">Image</option>
                <option value="video">Video</option>
                <option value="animation">GIF</option>
              </select>
            </div>
          </div>

          {/* Text 1 */}
          <div>
            <Label className="text-zinc-300 text-xs">Text 1 (separate message)</Label>
            <Textarea value={cfg.text1} onChange={(e) => set({ text1: e.target.value })} rows={3}
              placeholder="e.g. 👋 Welcome to our platform!"
              className="bg-zinc-950 border-zinc-800 text-zinc-100" data-testid="bot-welcome-text1" />
          </div>

          {/* Text 2 */}
          <div>
            <Label className="text-zinc-300 text-xs">Text 2 (separate message — buttons attach below this)</Label>
            <Textarea value={cfg.text2} onChange={(e) => set({ text2: e.target.value })} rows={3}
              placeholder="e.g. Choose an option below 👇"
              className="bg-zinc-950 border-zinc-800 text-zinc-100" data-testid="bot-welcome-text2" />
          </div>

          {/* Buttons */}
          <div className="space-y-2">
            <Label className="text-zinc-300 text-xs">Inline Buttons (label + link)</Label>
            {cfg.buttons.length === 0 && <div className="text-zinc-500 text-xs">No buttons yet.</div>}
            {cfg.buttons.map((b, i) => (
              <div key={i} className="flex items-center gap-2" data-testid={`bot-welcome-btn-row-${i}`}>
                <Input value={b.label} onChange={(e) => setBtn(i, { label: e.target.value })} placeholder="Button label"
                  className="bg-zinc-950 border-zinc-800 text-zinc-100 text-xs w-40" data-testid={`bot-welcome-btn-label-${i}`} />
                <Input value={b.url} onChange={(e) => setBtn(i, { url: e.target.value })} placeholder="https://…"
                  className="bg-zinc-950 border-zinc-800 text-zinc-100 text-xs flex-1" data-testid={`bot-welcome-btn-url-${i}`} />
                <button type="button" onClick={() => rmBtn(i)}
                  className="w-8 h-8 rounded-md bg-zinc-950 border border-zinc-800 text-red-400 hover:bg-red-600 hover:text-white flex items-center justify-center shrink-0"
                  data-testid={`bot-welcome-btn-remove-${i}`}>
                  <Trash2 size={13} />
                </button>
              </div>
            ))}
            <Button type="button" variant="outline" onClick={addBtn}
              className="border-zinc-700 bg-zinc-950 text-zinc-200 hover:bg-zinc-800" data-testid="bot-welcome-add-btn">
              <Plus size={14} className="mr-1.5" /> Add Button
            </Button>
          </div>

          <div className="flex justify-end">
            <Button onClick={save} disabled={saving} className="bg-[color:var(--gold)] text-black font-bold" data-testid="bot-welcome-save-btn">
              <Save size={14} className="mr-1.5" /> {saving ? 'Saving…' : 'Save Bot Setting'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
